import * as Haptics from 'expo-haptics';
import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { Analytics } from '@/utils/analytics';
import { createStyles } from '@/contexts/theme-context';
import { FONTS } from '@/styles';
import { formatCurrency } from '@/utils/money';
import { getTranslationValue } from '@/utils/translations';
import { GrowthPercentagePill } from '@/components/insights-hub/growth-percentage-pill';
import { HubCardBase } from '@/components/insights-hub/hub-card-base';
import { HubProfitRollupSectionSkeleton } from '@/components/insights-hub/hub-profit-rollup-section-skeleton';
import { Platform, Text, View } from 'react-native';
import { PriceTrendsDisplayType } from '@/enums';
import { toFixedDecimalPercentage } from '@/utils/percentage';
import { Toggle } from '@usepepper/ui/atoms/toggle';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useSupplierDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';
import { useWeeklyProfitRollupData } from '@/hooks/insights-hub/use-profit-rollup-data';

const formatValue = (
  value: number,
  currencyCode: string,
  displayMode: PriceTrendsDisplayType,
) => {
  switch (displayMode) {
    case PriceTrendsDisplayType.Price:
      return formatCurrency(value, currencyCode);
    case PriceTrendsDisplayType.GP:
      return toFixedDecimalPercentage(value);
    default:
      return String(value);
  }
};

const LARGE_DISPLAY_MONEY_UPPER_BOUND = 1e12;

export const HubProfitRollup: React.FC = () => {
  const { user, selectedRestaurantRelationship } =
    useAuthenticatedUserContext();
  const showGrossProfit = !useSupplierDynamicConfigFlag(
    'sales_rep_config',
    'disable_gross_profit_display',
  );
  const [selectedProfitTab, setSelectedProfitTab] =
    useState<PriceTrendsDisplayType>(PriceTrendsDisplayType.Price);

  const [styles] = useStyles();

  const { data, error, loading } = useWeeklyProfitRollupData();

  const onChangeToggle = (value: PriceTrendsDisplayType) => {
    if (Platform.OS !== 'web') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }
    setSelectedProfitTab(value);

    Analytics.track('insights_hub.weekly_profit_rollup.toggle.press', {
      selectedProfitTab: value,
      supplierUUID: selectedRestaurantRelationship.supplierUUID,
      userUUID: user.uuid,
    });
  };

  // cache policy is cache-and-network,
  //   loading will be true while cache data is displayed and network data is being retrieved
  if (loading && !data) {
    return Platform.OS === 'web' ? <HubProfitRollupSectionSkeleton /> : null;
  }

  if (!data || error) {
    return null;
  }

  return (
    <>
      <HubCardBase
        actionsElement={
          <View style={styles.toggle}>
            {data.avg_gp_percentage && showGrossProfit ? (
              <Toggle
                onPress={onChangeToggle}
                selectedTab={selectedProfitTab}
                tabs={[
                  {
                    label: i18n.t('price_trends.price'),
                    value: PriceTrendsDisplayType.Price,
                  },
                  {
                    label: i18n.t('price_trends.gross_profit'),
                    value: PriceTrendsDisplayType.GP,
                  },
                ]}
              />
            ) : null}
          </View>
        }
        description={getTranslationValue(data.subtitle)}
        title={getTranslationValue(data.title)}
      >
        <View style={styles.container}>
          <View style={styles.content}>
            <Text
              style={[
                FONTS.primarySemiBold,
                styles.mainQuantity,
                // if value too large (>$1M), reduce font size
                (selectedProfitTab === PriceTrendsDisplayType.Price ||
                  !showGrossProfit) &&
                data.total_sales_in_micros > LARGE_DISPLAY_MONEY_UPPER_BOUND
                  ? styles.mainQuantityTooLong
                  : null,
              ]}
            >
              {formatValue(
                selectedProfitTab === PriceTrendsDisplayType.Price ||
                  !showGrossProfit
                  ? data.total_sales_in_micros
                  : data.avg_gp_percentage || 0,
                data.currency_code,
                !showGrossProfit
                  ? PriceTrendsDisplayType.Price
                  : selectedProfitTab,
              )}
            </Text>
          </View>
          <View style={styles.content}>
            <GrowthPercentagePill
              value={
                selectedProfitTab === PriceTrendsDisplayType.Price ||
                !showGrossProfit
                  ? data.total_sales_percentage_delta || 0
                  : data.avg_gp_percentage_delta || 0
              }
            />
            <View collapsable={false}>
              <Text style={[FONTS.primary, styles.wowText]}>
                {i18n.t('insights_hub.weekly_profit_rollup.wow')}
              </Text>
            </View>
          </View>
        </View>
      </HubCardBase>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    gap: 8,
    justifyContent: 'center',
    marginVertical: 32,
    width: '100%',
  },
  content: {
    flexDirection: 'row',
    gap: 4,
    justifyContent: 'center',
  },
  mainQuantity: {
    fontSize: 40,
  },
  mainQuantityTooLong: {
    fontSize: 32,
  },
  toggle: {
    width: 100,
  },
  wowText: {
    color: theme.sk.color.text.secondary,
    flexDirection: 'row',
    fontSize: 14,
    justifyContent: 'flex-start',
    lineHeight: 16.8,
    width: '100%',
  },
}));
